import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useDropzone } from 'react-dropzone';
import fileFill from '@iconify/icons-eva/file-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { motion, AnimatePresence } from 'framer-motion';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import {
  Box,
  List,
  Link,
  Stack,
  Paper,
  Button,
  ListItem,
  Typography,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Card,
  Skeleton,
  makeStyles,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
// utils
import { fData } from '../../utils/formatNumber';
//
import { MIconButton } from '../@material-extend';
import { DialogAnimate, varFadeInRight } from '../animate';
import { UploadIllustration } from '../../assets';
import { PictureAsPdf, PictureAsPdfOutlined } from '@material-ui/icons';
import { LoadingButton } from '@material-ui/lab';
import { useState } from 'react';
import filePdfIcon from '@iconify/icons-mdi/file-pdf';

// ----------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  attachmentBox: {
      cursor: 'pointer',
      display: 'flex',
      borderRadius: '6px',
      backgroundColor: '#172e34',
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0),
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative', 
      height: '100px', 
      overflow: 'hidden'
  },
  attachmentIcon: {
      marginRight: theme.spacing(1),
  },
  delete:{
      m:2,
      display:'flex',
      justifyContent:'flex-end' 
    },
  checkbox: {
      position: 'absolute',
      top: theme.spacing(1), 
      right: theme.spacing(1), 
  },
  checkboxContainer: {
      position: 'absolute',
      top: theme.spacing(1),
      right: theme.spacing(1),
  },
  dialogContent: {
      padding: theme.spacing(3),
      maxWidth: 768,
      margin: 'auto',
      display: 'flex',
      flexDirection:'column',
      justifyContent: 'center',
      alignItems: 'center',
  },
  gridContainer: {
      height: 'auto',
  },
}));

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  display: 'flex',
  textAlign: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
  [theme.breakpoints.up('md')]: { textAlign: 'left', flexDirection: 'row' }
}));

// ----------------------------------------------------------------------

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  files: PropTypes.array,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  sx: PropTypes.object
};

export default function UploadMultiFile({ error, showPreview = false, files, onRemove, onRemoveAll,isUploading = false, sx, ...other }) {
  const hasFile = files?.length > 0;
  const classes = useStyles();
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [useOfficeViewer, setUseOfficeViewer] = useState(true);

    // Get user data from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    const isClient = user?.isClient;
    const userType = isClient ? 3 : user?.user_type;
    const userId = user?.user_id;
    
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: other.multiple ?? true,   // default: true
    ...other
  });

  const isSingle = other.multiple === false;

  const ShowRejectionItems = () => (
    <Paper
      variant="outlined"
      sx={{
        py: 1,
        px: 2,
        mt: 3,
        borderColor: 'error.light',
        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08)
      }}
    >
      {fileRejections?.map(({ file, errors }) => {
        const { path, size } = file;
        return (
          <Box key={path} sx={{ my: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {path} - {fData(size)}
            </Typography>
            {errors.map((e) => (
              <Typography key={e.code} variant="caption" component="p">
                - {e.message}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', ...sx }}>
      {/* {isUploading ?
        <LinearProgress variant='indeterminate' color='primary' />
      : */}
        <DropZoneStyle
          {...getRootProps()}
          sx={{
            ...(isDragActive && { opacity: 0.72 }),
            ...((isDragReject || error) && {
              color: 'error.main',
              borderColor: 'error.light',
              bgcolor: 'error.lighter'
            }),
            ...(isSingle && {
              padding: '24px 8px',          // ↓ Reduced padding
              minHeight: '140px'            // ↓ Shorter height
            })
          }}
        >
          <input {...getInputProps()} />

          <UploadIllustration sx={{ width: isSingle ? 140 : 220 }} />

          <Box sx={{ p: isSingle ? 1 : 3, ml: { md: isSingle ? 1 : 2 } }}>
            <Typography gutterBottom variant="h5">
              {isSingle ? "Drop or Select a file" : "Drop or Select files"}
            </Typography>

            <Typography variant="body2" >
            {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}> */}
              Drop {isSingle ? 'file' : 'files'} here or click&nbsp;
              <Link underline="always">browse</Link>&nbsp;through your machine
            </Typography>
            {/* <Typography variant="caption" sx={{ color: 'error.main' }}>
            Maximum file size: 15MB
            </Typography> */}
          </Box>
        </DropZoneStyle>
      {/* } */}
      {fileRejections?.length > 0 && <ShowRejectionItems />}

      <List disablePadding sx={{ ...(hasFile && { my: 3 }) }}>
        <AnimatePresence>
          {(isSingle ? files.slice(0, 1) : files)?.map((file) => {
            const { size, preview, file_type, extension } = file;
            const url = file.url || file.name;
            const key = isString(file) ? file : url;
            console.log("file=------",file)
            if (showPreview) {
              return (
                <ListItem
                  key={key}
                  component={motion.div}
                  {...varFadeInRight}
                  sx={{
                    p: 0,
                    m: 0.5,
                    width: 80,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'inline-flex'
                  }}
                >
                  {file_type === 'application/pdf' || extension === 'pdf' ? 
                    <>
                      <IconButton onClick={() => {
                        setPdfLoading(true);
                        setSelectedPDF(isString(url) ? url : preview);
                        setOpenPDFModal(true);
                        setUseOfficeViewer(true); // Reset to try Office viewer first
                      }}
                      aria-label="open pdf"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }} >
                        <PictureAsPdf style={{ fontSize: 40 }} />
                    </IconButton>
                    <DialogAnimate open={openPDFModal} onClose={() => setOpenPDFModal(false)}>
                      <Card className={classes.dialogContent}>
                        <div style={{width:'90%', display:'flex',justifyContent:'flex-end', marginBottom: '15px',marginLeft:'25px' }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => {
                                if (isClient || userType === 3) {
                                  // For clients/userType 3: Open PDF in new window without toolbar
                                  const newWindow = window.open('', '_blank');
                                  newWindow.document.write(`
                                    <html>
                                      <head>
                                        <title>PDF Viewer</title>
                                        <style>
                                          body { margin: 0; padding: 0; overflow: hidden; }
                                          iframe { width: 100vw; height: 100vh; border: none; }
                                        </style>
                                      </head>
                                      <body>
                                        <iframe src="${selectedPDF}#toolbar=0&navpanes=0&scrollbar=0" title="PDF Viewer"></iframe>
                                      </body>
                                    </html>
                                  `);
                                  newWindow.document.close();
                                } else {
                                  // For other users: Open PDF normally
                                  window.open(selectedPDF, '_blank');
                                }
                              }}
                              disabled={!selectedPDF}
                              startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
                              sx={{
                                fontSize:'14px'
                              }}
                            >
                              View PDF
                            </Button>
                        </div>
                        {pdfLoading && (
                          <Skeleton variant="rectangular" width="100%" height={500} />
                        )}
                        {(isClient || userType === 3) && useOfficeViewer ? (
                          <iframe
                            src={`https://docs.google.com/gview?url=${encodeURIComponent(selectedPDF)}&embedded=true&toolbar=0&navpanes=0&scrollbar=0`}
                            title="Selected PDF"
                            width="100%"
                            height="500px"
                            frameBorder="0"
                            style={{ display: pdfLoading ? 'none' : 'block' }}
                            onLoad={() => setPdfLoading(false)}
                            onError={() => {
                              // If Google Docs viewer fails, fallback to direct iframe
                              setUseOfficeViewer(false);
                              setPdfLoading(false);
                            }}
                          />
                        ) : (
                          <iframe
                            src={selectedPDF}
                            title="Selected PDF"
                            width="100%"
                            height="500px"
                            frameBorder="0"
                            style={{ display: pdfLoading ? 'none' : 'block' }}
                            onLoad={() => setPdfLoading(false)}
                          />
                        )}
                      </Card>
                    </DialogAnimate>
                    </> : 
                    <Paper
                      variant="outlined"
                      component="img"
                      src={isString(url) ? url : preview}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
                    />
                  }
                  <Box sx={{ top: 6, right: 6, position: 'absolute' }}>
                    <MIconButton
                      size="small"
                      onClick={() => onRemove(file)}
                      sx={{
                        p: '2px',
                        color: 'common.white',
                        bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                        '&:hover': {
                          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48)
                        }
                      }}
                    >
                      <Icon icon={closeFill} />
                    </MIconButton>
                  </Box>
                </ListItem>
              );
            }

            return (
              <ListItem
                key={key}
                component={motion.div}
                {...varFadeInRight}
                sx={{
                  my: 1,
                  py: 0.75,
                  px: 2,
                  borderRadius: 1,
                  border: (theme) => `solid 1px ${theme.palette.divider}`,
                  bgcolor: 'background.paper'
                }}
              >
                <ListItemIcon>
                  <Icon icon={fileFill} width={28} height={28} />
                </ListItemIcon>
                <ListItemText
                  primary={isString(file) ? file : url}
                  secondary={isString(file) ? '' : fData(size)}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <MIconButton edge="end" size="small" onClick={() => onRemove(file)}>
                    <Icon icon={closeFill} />
                  </MIconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </AnimatePresence>
      </List>

      {(hasFile && !isSingle) && (
        <Stack direction="row" justifyContent="flex-end">
          <Button onClick={onRemoveAll} sx={{ mr: 1.5 }}>
            Remove all
          </Button>
          <LoadingButton
            {...getRootProps()}
            variant="contained"
            loading={isUploading}
            >
            Upload files
          </LoadingButton>
          {/* <Button {...getRootProps()} variant="contained">Upload files</Button> */}
        </Stack>
      )}
    </Box>
  );
}
